import { useEffect, useRef, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Building2, Receipt, Phone, Store, X, Check } from "lucide-react";
import { toast } from "sonner";
import { ask } from "@/lib/confirm";
import { IcoInput } from "@/components/shared/IcoInput";

const EMPTY = { name: "", ico: "", dic: "", street: "", zip: "", city: "", contact_person: "", email: "", phone: "", web: "", note: "", branches: [] };
const fromSupplier = (s) => ({ ...EMPTY, ...Object.fromEntries(Object.entries(s || {}).filter(([k, v]) => k in EMPTY && v != null)),
  branches: (s?.branches || []).map((b) => b.name) });

// Karta jako ve formuláři investora (Sec) — hlavička s brand ikonou, pole uvnitř na bg-card.
function Sec({ icon: Icon, title, children }) {
  return (
    <section className="bg-secondary/60 border border-border rounded-[14px] p-4 space-y-3.5 min-w-0 [&_input]:bg-card [&_textarea]:bg-card [&_input]:placeholder:text-muted-foreground/55 [&_textarea]:placeholder:text-muted-foreground/55">
      <h3 className="flex items-center gap-2 font-heading font-extrabold text-[12px] uppercase tracking-[0.07em]">
        {Icon && <Icon size={16} className="text-brand shrink-0" />}{title}
      </h3>
      {children}
    </section>
  );
}

const F = ({ label, children, className = "" }) => (
  <div className={`min-w-0 ${className}`}><Label>{label}</Label><div className="mt-1">{children}</div></div>
);

// 314: nový / upravit dodavatele (jako investor). IČO načte firmu z ARES; pobočky slouží
// objednávkám s osobním odběrem. `prefill` = jméno napsané v objednávce.
export function SupplierEditDialog({ open, onOpenChange, supplier, prefill = "", onSaved }) {
  const edit = !!supplier?.id;
  const [f, setF] = useState(EMPTY);
  const [branch, setBranch] = useState("");
  const [busy, setBusy] = useState(false);
  const [tried, setTried] = useState(false);
  const initial = useRef("");

  useEffect(() => {
    if (!open) return;
    const init = edit ? fromSupplier(supplier) : { ...EMPTY, name: prefill };
    initial.current = JSON.stringify(init);
    setF(init); setBranch(""); setTried(false); setBusy(false);
  }, [open, supplier?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const dirty = initial.current !== JSON.stringify(f);
  const addBranch = () => {
    const b = branch.trim();
    if (b && !f.branches.includes(b)) setF((p) => ({ ...p, branches: [...p.branches, b] }));
    setBranch("");
  };

  const save = async () => {
    if (!f.name.trim()) { setTried(true); return; }
    const body = { ...f, branches: branch.trim() && !f.branches.includes(branch.trim()) ? [...f.branches, branch.trim()] : f.branches };
    setBusy(true);
    try {
      const r = edit ? await api.patch(`/suppliers/${supplier.id}`, body) : await api.post("/suppliers", body);
      toast.success(edit ? "Dodavatel uložen." : `Dodavatel ${r.data.name} přidán.`);
      onSaved?.(r.data);
      onOpenChange(false);
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const close = async (next) => {
    if (next) return onOpenChange(true);
    if (dirty && !await ask("Máš neuložené změny. Zahodit?", { ok: "Zahodit změny", danger: true })) return;
    onOpenChange(false);
  };

  const inp = "h-12 md:h-11 w-full min-w-0 text-base md:text-sm";
  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent sheet data-testid="supplier-dialog" onOpenAutoFocus={(e) => e.preventDefault()}
        onKeyDown={(e) => { if ((e.metaKey || e.ctrlKey) && e.key === "Enter") { e.preventDefault(); save(); } }}
        className="max-w-[680px] w-[calc(100%-1.5rem)] sm:w-full p-0 gap-0 flex flex-col max-h-[92dvh] overflow-hidden">
        <div className="shrink-0 px-5 md:px-6 py-4 border-b border-border pr-14">
          <DialogTitle className="text-xl md:text-2xl font-black tracking-tight">{edit ? "Upravit dodavatele" : "Nový dodavatel"}</DialogTitle>
          <DialogDescription className="mt-0.5">Povinný je jen název. IČO doplní zbytek z registru ARES.</DialogDescription>
        </div>

        <div className="flex-1 overflow-y-auto px-5 md:px-6 py-5 space-y-4 min-w-0">
          <Sec icon={Building2} title="Kdo to je">
            <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr] gap-3.5">
              <F label="IČO">
                <IcoInput value={f.ico} onChange={(v) => setF((p) => ({ ...p, ico: v }))} className={inp} testid="supplier-ico"
                  onFound={(d) => setF((p) => ({ ...p, name: d.name || p.name, dic: d.dic || p.dic, street: d.street || p.street, zip: d.zip || p.zip, city: d.city || p.city }))} />
              </F>
              <F label={<>Název firmy <span className="text-brand">*</span></>}>
                <Input value={f.name} onChange={set("name")} placeholder="SIKO KOUPELNY a.s." data-testid="supplier-name"
                  className={`${inp} font-semibold ${tried && !f.name.trim() ? "border-destructive" : ""}`} />
              </F>
            </div>
            {tried && !f.name.trim() && <p className="text-[13px] text-destructive">Bez názvu dodavatele neuložíš.</p>}
          </Sec>

          <Sec icon={Receipt} title="Fakturační údaje">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-3.5">
              <F label="DIČ"><Input value={f.dic} onChange={set("dic")} placeholder="CZ25011120" className={`${inp} tabular-nums`} data-testid="supplier-dic" /></F>
              <F label="Ulice a č. p."><Input value={f.street} onChange={set("street")} placeholder="Bucharova 2641/14" className={inp} data-testid="supplier-street" /></F>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_2fr] gap-3.5">
              <F label="PSČ"><Input value={f.zip} onChange={set("zip")} inputMode="numeric" placeholder="158 00" className={`${inp} tabular-nums`} data-testid="supplier-zip" /></F>
              <F label="Město"><Input value={f.city} onChange={set("city")} placeholder="Praha 5" className={inp} data-testid="supplier-city" /></F>
            </div>
          </Sec>

          <Sec icon={Phone} title="Kontakt">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
              <F label="Kontaktní osoba"><Input value={f.contact_person} onChange={set("contact_person")} placeholder="Jan Novák" className={inp} data-testid="supplier-contact" /></F>
              <F label="Telefon"><Input value={f.phone} onChange={set("phone")} inputMode="tel" placeholder="+420 777 123 456" className={`${inp} tabular-nums`} data-testid="supplier-phone" /></F>
              <F label="E-mail"><Input value={f.email} onChange={set("email")} inputMode="email" placeholder="objednavky@firma.cz" className={inp} data-testid="supplier-email" /></F>
              <F label="Web / e-shop"><Input value={f.web} onChange={set("web")} inputMode="url" placeholder="www.siko.cz" className={inp} data-testid="supplier-web" /></F>
            </div>
            <F label="Poznámka">
              <Textarea value={f.note} onChange={set("note")} rows={2} placeholder="Sleva, zákaznické číslo, kdo vyřizuje…" className="w-full min-w-0 text-base md:text-sm" data-testid="supplier-note" />
            </F>
          </Sec>

          <Sec icon={Store} title="Pobočky na vyzvednutí">
            {f.branches.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid="supplier-branches">
                {f.branches.map((b) => (
                  <span key={b} className="h-9 pl-3 pr-1.5 rounded-full bg-card border border-border text-[13.5px] font-semibold inline-flex items-center gap-1">
                    {b}
                    <button type="button" onClick={() => setF((p) => ({ ...p, branches: p.branches.filter((x) => x !== b) }))} aria-label={`Odebrat pobočku ${b}`}
                      className="w-7 h-7 grid place-items-center rounded-full text-muted-foreground hover:text-destructive"><X size={14} /></button>
                  </span>
                ))}
              </div>
            )}
            <div className="flex gap-2">
              <Input value={branch} onChange={(e) => setBranch(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addBranch(); } }}
                placeholder="např. SIKO Černý Most" className={`${inp} flex-1`} data-testid="supplier-branch-input" />
              <Button type="button" variant="outline" onClick={addBranch} disabled={!branch.trim()} className="h-12 md:h-11 font-bold shrink-0" data-testid="supplier-branch-add">Přidat</Button>
            </div>
            <p className="text-[12.5px] text-muted-foreground">Nabídnou se v objednávce u osobního odběru.</p>
          </Sec>
        </div>

        <div className="shrink-0 px-5 md:px-6 py-3.5 border-t border-border flex flex-col-reverse gap-2 sm:flex-row sm:justify-end pb-[max(14px,env(safe-area-inset-bottom))] sm:pb-3.5">
          <Button variant="outline" onClick={() => close(false)} className="h-12 md:h-11 w-full sm:w-auto font-bold" data-testid="supplier-cancel">Zrušit</Button>
          <Button onClick={save} disabled={busy} className="h-12 md:h-11 w-full sm:w-auto font-bold px-6" data-testid="supplier-save">
            {busy ? "Ukládám…" : edit ? <><Check size={16} /> Uložit změny</> : "Přidat dodavatele"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
